import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { documentApi } from 'src/api/document';
import { medicationApi } from 'src/api/medication';
import { organizationProfileApi } from 'src/api/organization-profile';
import { reminderApi } from 'src/api/reminder';
import { treatmentApi } from 'src/api/treatment';
import { visitScopedPrescribingApi } from 'src/api/visit-scoped-prescribing';
import VetsiIcon from 'src/assets/icons/VetsiIcon';
import Iconify from 'src/components/iconify';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import type { FilesUploadModalFormValuesProps } from 'src/components/files-upload-modal';
import type { MedicationsFormModalValuesProps } from 'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal';
import type { RemindersFormSubmitValues } from 'src/sections/@dashboard/profile/components/ProfileRemindersFormModal';
import type { TreatmentFormSubmitValues } from 'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal';
import type { TreatmentFormSnapshot } from 'src/sections/@dashboard/profile/components/ProfileTreatments/treatmentFormValues';
import {
  AddProfileTreatmentDto,
  CreateReminderDto,
  UpdateProfileTreatmentDto,
  UpdateReminderDto,
  VisitActivityResponseDtoActionEnum,
  VisitActivityResponseDtoTypeEnum,
} from 'woofwoof-api';
import {
  facilityMedicationBody,
  useMedicationCreateSeries,
  useMedicationEditSeries,
} from '../hooks/useMedicationWriteSeries';
import {
  useTreatmentCreateSeries,
  useTreatmentEditSeries,
} from '../hooks/useTreatmentWriteSeries';
import { editFormReady } from '../utils/editFormReady';
import { useClinicalQuery } from 'src/sections/@dashboard/visit/hooks/useClinicalQuery';
import { patientOrVisitRoute } from 'src/sections/@dashboard/warehouse/hooks/useClinicalWriteSeries';
import { useStockWriteError } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteError';
import { useStockWriteFeedback } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteFeedback';
import { translateKey } from 'src/locales/translate';
import { forbiddenMessage } from 'src/utils/forbiddenMessage';
import PatientNoteModal from 'src/sections/@dashboard/profile/components/PatientNoteModal/PatientNoteModal';
import { visitApi } from 'src/api/visit';
import { CARD_HEADER_SX, CARD_HEADER_TITLE_PROPS } from 'src/theme/cardHeader';

// Lazy-load the modals — their form code (Yup schema compile, react-hook-form
// setup, MUI form controls) is non-trivial in dev mode. Splitting them into
// separate chunks keeps the visit page load light; we pre-warm the chunks on
// idle so first open still feels instant.
const MedicationFormModal = lazy(
  () =>
    import(
      'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal'
    ),
);
const ProfileTreatmentFormModal = lazy(
  () =>
    import(
      'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal'
    ),
);
const ProfileRemindersFormModal = lazy(
  () =>
    import(
      'src/sections/@dashboard/profile/components/ProfileRemindersFormModal'
    ),
);
const FilesUploadModal = lazy(
  () => import('src/components/files-upload-modal'),
);

type ActiveModal =
  | 'medication'
  | 'treatment'
  | 'reminder'
  | 'document'
  | 'note'
  | null;

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  [VisitActivityResponseDtoTypeEnum.Medication]: 'Lek',
  [VisitActivityResponseDtoTypeEnum.Treatment]: translateKey(
    'visit.visitCourseMenuTreatment',
  ),
  [VisitActivityResponseDtoTypeEnum.Reminder]: translateKey(
    'visit.visitCourseMenuReminder',
  ),
  [VisitActivityResponseDtoTypeEnum.Document]: translateKey(
    'visit.visitCourseMenuDocument',
  ),
  TRANSCRIPTION: 'Transkrypcja',
};

const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  [VisitActivityResponseDtoActionEnum.Added]: 'Dodano',
  [VisitActivityResponseDtoActionEnum.Updated]: 'Zaktualizowano',
  [VisitActivityResponseDtoActionEnum.Removed]:
    translateKey('hardcoded.usunieto'),
  STARTED: 'Rozpoczęto',
  STOPPED: 'Zakończono',
};

export default function VisitCourseCard() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { visitId, patientId } = useParams<{
    visitId: string;
    patientId: string;
  }>();
  const queryClient = useQueryClient();
  const writeError = useStockWriteError();
  // A Warehouse refusal, else the backend's reason for a 403 (#707: a visit
  // past its edit window), else the card's own copy.
  const refusal =
    (fallback: string) =>
    (error: unknown): void =>
      writeError(forbiddenMessage(error) || fallback)(error);
  const onStockWrite = useStockWriteFeedback();
  const { selectedOrganization, isProfessionalRoleSelected } =
    useOrganizationContext();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [editingReferenceId, setEditingReferenceId] = useState<string | null>(
    null,
  );
  const isGuestVisit = !patientId;

  // Pre-warm the lazy-loaded modal chunks during idle time so first open of
  // any "Dodaj …" item feels instant, in both dev and production.
  useEffect(() => {
    const preload = () => {
      import(
        'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal'
      );
      import(
        'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal'
      );
      import(
        'src/sections/@dashboard/profile/components/ProfileRemindersFormModal'
      );
      import('src/components/files-upload-modal');
    };
    const ric = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void) => number;
        cancelIdleCallback?: (handle: number) => void;
      }
    ).requestIdleCallback;
    if (typeof ric === 'function') {
      const handle = ric(preload);
      return () => {
        (
          window as unknown as { cancelIdleCallback?: (handle: number) => void }
        ).cancelIdleCallback?.(handle);
      };
    }
    const timer = setTimeout(preload, 500);
    return () => clearTimeout(timer);
  }, []);

  const { data, isLoading } = useQuery(
    ['visit-activity', visitId],
    () =>
      organizationProfileApi.organizationProfileControllerGetVisitActivity({
        visitId: visitId || '',
      }),
    { enabled: Boolean(visitId) },
  );

  // R1-1: the medication and treatment lists carry the selected clinic's
  // stock issues and materials, so their keys are that clinic's.
  const { data: medicationsData } = useClinicalQuery(
    'patient-medications',
    patientId,
    [],
    (options) =>
      medicationApi.medicationControllerGetProfileMedicationsInOrganization(
        { profileId: patientId || '' },
        options,
      ),
    { enabled: Boolean(patientId) },
  );

  const { data: treatmentsData } = useClinicalQuery(
    'profile-treatments',
    patientId,
    [],
    (options) =>
      treatmentApi.treatmentRecordControllerGetProfileTreatments(
        { profileId: patientId || '' },
        options,
      ),
    { enabled: Boolean(patientId) },
  );

  const { data: remindersData } = useQuery(
    ['profile-reminders', patientId],
    () =>
      reminderApi.reminderControllerGetProfileReminders({
        profileId: patientId || '',
      }),
    { enabled: Boolean(patientId) },
  );

  const { data: catalogData } = useQuery(
    ['treatment-catalog', selectedOrganization?.id],
    () =>
      treatmentApi.treatmentControllerGetCatalog({
        organizationId: selectedOrganization!.id,
      }),
    { enabled: Boolean(selectedOrganization?.id) },
  );

  // Guest-visit lists for edit-form pre-fill (no profile to query against).
  const { data: visitMedicationsData } = useClinicalQuery(
    'visit-medications',
    visitId,
    [],
    (options) =>
      visitScopedPrescribingApi.listMedications(visitId || '', options),
    { enabled: isGuestVisit && Boolean(visitId) },
  );
  const { data: visitTreatmentsData } = useClinicalQuery(
    'visit-treatments',
    visitId,
    [],
    (options) =>
      visitScopedPrescribingApi.listTreatments(visitId || '', options),
    { enabled: isGuestVisit && Boolean(visitId) },
  );
  const { data: visitRemindersData } = useQuery(
    ['visit-reminders', visitId],
    () => visitScopedPrescribingApi.listReminders(visitId || ''),
    { enabled: isGuestVisit && Boolean(visitId) },
  );

  const invalidateAndClose = (extraKeys: string[][]) => {
    extraKeys.forEach((key) => queryClient.invalidateQueries(key));
    queryClient.invalidateQueries(['visit-activity', visitId]);
    if (isGuestVisit) {
      queryClient.invalidateQueries(['visit-medications', visitId]);
      queryClient.invalidateQueries(['visit-treatments', visitId]);
      queryClient.invalidateQueries(['visit-reminders', visitId]);
    }
    setActiveModal(null);
    setEditingReferenceId(null);
  };

  const { mutate: removeActivity } = useMutation({
    mutationFn: (activityId: string) =>
      organizationProfileApi.organizationProfileControllerRemoveVisitActivity({
        visitId: visitId || '',
        activityId,
      }),
    // A removed medication or treatment reverses its stock.
    onSuccess: (response) => {
      onStockWrite(response);
      queryClient.invalidateQueries(['visit-activity', visitId]);
      queryClient.invalidateQueries(['patient-medications', patientId]);
      queryClient.invalidateQueries(['profile-treatments', patientId]);
      queryClient.invalidateQueries(['profile-reminders', patientId]);
      queryClient.invalidateQueries(['patient-documents-list', patientId]);
      if (isGuestVisit) {
        queryClient.invalidateQueries(['visit-medications', visitId]);
        queryClient.invalidateQueries(['visit-treatments', visitId]);
        queryClient.invalidateQueries(['visit-reminders', visitId]);
      }
      enqueueSnackbar(t('visit.visitCourseRemoveSuccess'), {
        variant: 'success',
      });
    },
    // M-1: a refused removal says why (e.g. the drug came from a treatment).
    onError: refusal(t('visit.visitCourseRemoveError')),
  });

  // --- Write series (R3-4); a guest visit writes through the visit ---
  const writeRoute = patientOrVisitRoute(patientId, visitId);
  const onMedicationSaved = (): void => {
    // R4-1: a reminder set on the form commits with the medication.
    invalidateAndClose([
      ['patient-medications', patientId || ''],
      ['profile-reminders', patientId || ''],
    ]);
    enqueueSnackbar(t('visit.visitCourseAddSuccess'), { variant: 'success' });
  };
  const addMedicationSeries = useMedicationCreateSeries({
    route: writeRoute,
    onSuccess: onMedicationSaved,
    onError: refusal(t('visit.visitCourseAddError')),
  });
  const updateMedicationSeries = useMedicationEditSeries({
    route: writeRoute,
    editingId: editingReferenceId ?? '',
    onSuccess: onMedicationSaved,
    onError: refusal(t('visit.visitCourseAddError')),
  });
  const medicationSeries = editingReferenceId
    ? updateMedicationSeries
    : addMedicationSeries;

  // --- Treatment write series (R3-4) ---
  const onTreatmentSaved = (): void => {
    invalidateAndClose([
      ['profile-treatments', patientId || ''],
      ['profile-reminders', patientId || ''],
    ]);
    enqueueSnackbar(t('visit.visitCourseAddSuccess'), { variant: 'success' });
  };
  const addTreatmentSeries = useTreatmentCreateSeries({
    route: writeRoute,
    onSuccess: onTreatmentSaved,
    onError: refusal(t('visit.visitCourseAddError')),
  });
  const updateTreatmentSeries = useTreatmentEditSeries({
    route: writeRoute,
    editingId: editingReferenceId ?? '',
    onSuccess: onTreatmentSaved,
    onError: refusal(t('visit.visitCourseAddError')),
  });
  const treatmentSeries = editingReferenceId
    ? updateTreatmentSeries
    : addTreatmentSeries;

  // --- Reminder mutations ---
  const { mutate: addReminder, isLoading: isAddingReminder } = useMutation({
    mutationFn: (payload: CreateReminderDto) =>
      isGuestVisit
        ? visitScopedPrescribingApi.addReminder(visitId || '', payload)
        : reminderApi.reminderControllerCreateReminder({
            createReminderDto: payload,
          }),
    onSuccess: () => {
      invalidateAndClose([['profile-reminders', patientId || '']]);
      enqueueSnackbar(t('visit.visitCourseAddSuccess'), { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(t('visit.visitCourseAddError'), { variant: 'error' });
    },
  });

  const { mutate: updateReminder, isLoading: isUpdatingReminder } = useMutation(
    {
      mutationFn: (payload: UpdateReminderDto) =>
        isGuestVisit
          ? visitScopedPrescribingApi.updateReminder(
              visitId || '',
              editingReferenceId || '',
              payload,
            )
          : reminderApi.reminderControllerUpdateReminder({
              reminderId: editingReferenceId || '',
              updateReminderDto: { ...payload, visitId: visitId || undefined },
            }),
      onSuccess: () => {
        invalidateAndClose([['profile-reminders', patientId || '']]);
        enqueueSnackbar(t('visit.visitCourseAddSuccess'), {
          variant: 'success',
        });
      },
      onError: () => {
        enqueueSnackbar(t('visit.visitCourseAddError'), { variant: 'error' });
      },
    },
  );

  // --- Document mutation ---
  const { mutateAsync: uploadDocuments, isLoading: isUploadingDocument } =
    useMutation({
      mutationFn: (files: FilesUploadModalFormValuesProps['files']) =>
        isGuestVisit
          ? visitScopedPrescribingApi.addDocuments(visitId || '', files || [])
          : documentApi.documentControllerAddProfileDocumentsInOrganization({
              profileId: patientId || '',
              documentFileDto: files || [],
              visitId: visitId || undefined,
            }),
      onSuccess: () => {
        invalidateAndClose([['patient-documents-list', patientId || '']]);
        enqueueSnackbar(t('visit.visitCourseAddSuccess'), {
          variant: 'success',
        });
      },
      onError: () => {
        enqueueSnackbar(t('visit.visitCourseAddError'), { variant: 'error' });
      },
    });

  // --- Text note mutation ---
  const { mutateAsync: createVisitTextNote, isLoading: isCreatingNote } =
    useMutation({
      mutationFn: (content: string) =>
        visitApi.visitControllerCreateVisitTextNote({
          visitId: visitId || '',
          createTextNoteDto: { content },
        }),
      onSuccess: () => {
        invalidateAndClose([['patient-documents-list', patientId || '']]);
        enqueueSnackbar(translateKey('hardcoded.notatka_zostala_dodana'), {
          variant: 'success',
        });
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.response?.data?.message ||
            translateKey('hardcoded.wystapil_blad_podczas_zapisywania_notatki'),
          { variant: 'error' },
        );
      },
    });

  // --- Handlers ---
  const handleOpenAdd = (type: ActiveModal) => {
    setEditingReferenceId(null);
    setActiveModal(type);
    setMenuAnchor(null);
  };

  const handleOpenEdit = (type: string, referenceId: string) => {
    setEditingReferenceId(referenceId);
    const modalType =
      type === VisitActivityResponseDtoTypeEnum.Medication
        ? 'medication'
        : type === VisitActivityResponseDtoTypeEnum.Treatment
        ? 'treatment'
        : type === VisitActivityResponseDtoTypeEnum.Reminder
        ? 'reminder'
        : null;
    setActiveModal(modalType);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setEditingReferenceId(null);
  };

  const handleMedicationSubmit = (
    formData: MedicationsFormModalValuesProps,
  ) => {
    const payload = facilityMedicationBody(formData);
    if (editingReferenceId) {
      updateMedicationSeries.submit(editingReferenceId, payload, formData);
    } else {
      addMedicationSeries.submit(null, payload, formData);
    }
  };

  const handleTreatmentSubmit = (
    values: TreatmentFormSubmitValues,
    shown: TreatmentFormSnapshot,
  ) => {
    // Only a create names its clinic; an edit's DTO has no such field.
    if (editingReferenceId) {
      updateTreatmentSeries.submit(
        editingReferenceId,
        values as UpdateProfileTreatmentDto,
        shown,
      );
    } else {
      const payload = {
        ...values,
        ...(selectedOrganization?.id
          ? { organizationId: selectedOrganization.id }
          : {}),
      };
      addTreatmentSeries.submit(null, payload as AddProfileTreatmentDto, shown);
    }
  };

  const handleReminderSubmit = (values: RemindersFormSubmitValues) => {
    if (editingReferenceId) {
      updateReminder(values as UpdateReminderDto);
    } else {
      addReminder({
        ...values,
        ...(patientId ? { profileId: patientId } : {}),
        ...(selectedOrganization?.id
          ? { organizationId: selectedOrganization.id }
          : {}),
        ...(visitId ? { visitId } : {}),
      } as CreateReminderDto);
    }
  };

  const handleDocumentUpload = async (
    files: FilesUploadModalFormValuesProps['files'],
  ) => {
    if (files) {
      await uploadDocuments(files);
    }
  };

  // --- Entity lookups for edit mode ---
  // Guest and authed lists share the same DTO shape (id-keyed), so the lookup
  // is uniform — the only difference is the source array.
  // The authed endpoints answer with a paged envelope; asked without paging
  // parameters (as here) it holds every row. The guest ones are still plain
  // arrays, hence the two shapes.
  const editingMedication = editingReferenceId
    ? (isGuestVisit
        ? visitMedicationsData?.data
        : medicationsData?.data.data
      )?.find((m) => m.id === editingReferenceId)
    : undefined;

  const editingTreatment = editingReferenceId
    ? (isGuestVisit
        ? visitTreatmentsData?.data
        : treatmentsData?.data.data
      )?.find((t) => t.id === editingReferenceId)
    : undefined;

  const editingReminder = editingReferenceId
    ? (isGuestVisit
        ? visitRemindersData?.data
        : remindersData?.data.data
      )?.find((r) => r.id === editingReferenceId)
    : undefined;

  // M-4: an edit form opens only once its entry is loaded.
  const formReady = editFormReady(
    editingReferenceId,
    activeModal === 'medication'
      ? editingMedication
      : activeModal === 'treatment'
      ? editingTreatment
      : editingReminder,
  );

  const items = data?.data || [];

  return (
    <>
      <Stack
        sx={{
          backgroundColor: theme.palette.background.default,
          borderRadius: 2,
          boxShadow: theme.customShadows.z8,
        }}
      >
        <Stack sx={CARD_HEADER_SX}>
          <Typography {...CARD_HEADER_TITLE_PROPS}>
            {t('visit.visitCourseTitle')}
          </Typography>
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            <Button
              sx={{
                background: theme.palette.common.black,
                color: theme.palette.common.white,
              }}
              variant="contained"
              size="small"
              startIcon={<Iconify icon="eva:plus-fill" width={16} />}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              {t('visit.visitCourseAdd')}
            </Button>
          </Stack>
        </Stack>
        <Box>
          <Stack sx={{ px: 2, py: 2, gap: 0 }}>
            {isLoading &&
              [0, 1, 2].map((i) => (
                <Stack
                  key={i}
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                    py: 1,
                  }}
                >
                  <Skeleton variant="circular" width={16} height={16} />
                  <Stack sx={{ gap: 0.5, flex: 1, ml: 1 }}>
                    <Stack
                      sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Skeleton variant="text" width={80} height={20} />
                      <Skeleton variant="text" width={35} height={16} />
                    </Stack>
                    <Skeleton variant="text" width="60%" height={18} />
                  </Stack>
                </Stack>
              ))}
            {!isLoading && items.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('visit.visitCourseEmpty')}
              </Typography>
            )}
            {!isLoading &&
              [...items].reverse().map((item, index) => {
                const isLast = index === items.length - 1;
                const canEdit =
                  item.action === VisitActivityResponseDtoActionEnum.Added ||
                  item.action === VisitActivityResponseDtoActionEnum.Updated;
                const canRemove =
                  item.action === VisitActivityResponseDtoActionEnum.Added;
                const isEditableType =
                  item.type !== VisitActivityResponseDtoTypeEnum.Document;
                // On/off events (e.g. transcription) have no named item to append.
                const showName =
                  item.action === VisitActivityResponseDtoActionEnum.Added ||
                  item.action === VisitActivityResponseDtoActionEnum.Updated ||
                  item.action === VisitActivityResponseDtoActionEnum.Removed;

                return (
                  <Stack
                    key={item.id}
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      mx: -1,
                      px: 1,
                      borderRadius: 1,
                      transition: 'background-color 0.15s',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Stack
                      sx={{
                        alignItems: 'center',
                        width: 28,
                        flexShrink: 0,
                        position: 'relative',
                        justifyContent: 'center',
                      }}
                    >
                      {index > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            width: 2,
                            top: 0,
                            bottom: '50%',
                            backgroundColor: theme.palette.divider,
                          }}
                        />
                      )}
                      {!isLast && (
                        <Box
                          sx={{
                            position: 'absolute',
                            width: 2,
                            top: '50%',
                            bottom: 0,
                            backgroundColor: theme.palette.divider,
                          }}
                        />
                      )}
                      <VetsiIcon
                        width={16}
                        height={16}
                        sx={{ flexShrink: 0, zIndex: 1 }}
                      />
                    </Stack>

                    <Stack
                      sx={{
                        flex: 1,
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                        py: 1,
                        ml: 1,
                        minWidth: 0,
                      }}
                    >
                      <Stack
                        sx={{
                          gap: 0.25,
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <Stack
                          sx={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {ACTIVITY_TYPE_LABELS[item.type] || item.type}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {format(new Date(item.createdAt), 'HH:mm')}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {ACTIVITY_ACTION_LABELS[item.action] || item.action}
                          {showName ? `: ${item.name}` : ''}
                        </Typography>
                      </Stack>

                      {canEdit && isEditableType && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleOpenEdit(item.type, item.referenceId)
                          }
                          sx={{
                            color: theme.palette.text.disabled,
                            flexShrink: 0,
                            '&:hover': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          <Iconify icon="eva:edit-2-outline" width={18} />
                        </IconButton>
                      )}

                      {canRemove && (
                        <IconButton
                          size="small"
                          onClick={() => removeActivity(item.id)}
                          sx={{
                            color: theme.palette.text.disabled,
                            flexShrink: 0,
                            '&:hover': {
                              color: theme.palette.error.main,
                            },
                          }}
                        >
                          <Iconify icon="eva:trash-2-outline" width={18} />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                );
              })}
          </Stack>
        </Box>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleOpenAdd('medication')}>
          {t('visit.visitCourseMenuMedication')}
        </MenuItem>
        <MenuItem onClick={() => handleOpenAdd('treatment')}>
          {t('visit.visitCourseMenuTreatment')}
        </MenuItem>
        <MenuItem onClick={() => handleOpenAdd('reminder')}>
          {t('visit.visitCourseMenuReminder')}
        </MenuItem>
        <MenuItem onClick={() => handleOpenAdd('document')}>
          {t('visit.visitCourseMenuDocument')}
        </MenuItem>
        <MenuItem onClick={() => handleOpenAdd('note')}>
          {translateKey('hardcoded.notatka')}
        </MenuItem>
      </Menu>

      <Suspense fallback={null}>
        {activeModal === 'medication' && formReady && (
          <MedicationFormModal
            isModalOpen={true}
            closeModal={handleCloseModal}
            medication={editingMedication}
            linkedReminder={editingMedication?.linkedReminder}
            onSubmit={handleMedicationSubmit}
            isCreating={addMedicationSeries.isLoading}
            isUpdating={updateMedicationSeries.isLoading}
            singleReminderOnly={isGuestVisit}
            stockEnabled
            unconfirmedSave={medicationSeries.pending}
            unconfirmedBlocked={medicationSeries.blocked}
            unconfirmedValues={medicationSeries.frozenValues}
            stockWriteError={medicationSeries.lastError}
            ownerProfileId={patientId || undefined}
          />
        )}
        {activeModal === 'treatment' && formReady && (
          <ProfileTreatmentFormModal
            isModalOpen={true}
            closeModal={handleCloseModal}
            treatment={editingTreatment}
            onSubmit={handleTreatmentSubmit}
            onDelete={() => {}}
            stockEnabled
            stockEditable={isProfessionalRoleSelected ?? false}
            unconfirmedSave={treatmentSeries.pending}
            unconfirmedBlocked={treatmentSeries.blocked}
            unconfirmedValues={treatmentSeries.frozenValues}
            isProfessional={isProfessionalRoleSelected ?? false}
            organizationId={selectedOrganization?.id}
            catalogOptions={(catalogData?.data as any) ?? []}
            isCreating={addTreatmentSeries.isLoading}
            isUpdating={updateTreatmentSeries.isLoading}
            isDeleting={false}
            singleReminderOnly={isGuestVisit}
          />
        )}
        {activeModal === 'reminder' && formReady && (
          <ProfileRemindersFormModal
            isModalOpen={true}
            closeModal={handleCloseModal}
            reminder={editingReminder}
            onSubmit={handleReminderSubmit}
            onDelete={() => {}}
            isCreating={isAddingReminder}
            isUpdating={isUpdatingReminder}
            isDeleting={false}
            singleReminderOnly={isGuestVisit}
          />
        )}
        {activeModal === 'document' && (
          <FilesUploadModal
            isOpen={true}
            onClose={handleCloseModal}
            onUpload={handleDocumentUpload}
            isLoading={isUploadingDocument}
            profileId={patientId}
            visitId={visitId}
          />
        )}
      </Suspense>
      <PatientNoteModal
        isOpen={activeModal === 'note'}
        onClose={handleCloseModal}
        mode="create"
        profileId={patientId || ''}
        onSubmit={async (content) => {
          await createVisitTextNote(content);
        }}
        isSaving={isCreatingNote}
        canUseAssistant={false}
        enableDictation={!!isProfessionalRoleSelected}
      />
    </>
  );
}
